import React from 'react';
import includes from 'lodash/includes';
import pick from 'lodash/pick';
import Box from '@material-ui/core/Box';

import type { PartnerName } from '@jerry-serverless/analyticslib/partner';
import { InternalSource } from '@jerry-serverless/analyticslib/types';
import { FlowType } from '@jerry-serverless/types';

import { ABTestCarValueAdvanceDownload } from '../../../utils/enabled_features/ab_tests';
import { AppStepSection, type AppData } from '../../../types/app_data';
import type { AppContainerProps } from '../../../components/app_container/AppContainer';
import { getPartnerLogo } from '../../../components/partner_logo/PartnerLogo';
import { PartnerFooter } from '../../../components/partner_footer/PartnerFooter';
import { CUSTOM_LANDING_ENABLED_LIST } from '../index';
import { JerryFooter } from '../../../components/app_container/components/footer/JerryFooter';
import { Logo } from '../../../components/logo/Logo';
import { isGarageFlow } from '../../garage';
import { growthBookInstance } from '../../../utils/analytics/growthbook';
import { DeviceTypeType } from '../../../types/app_data';

export const PARTNERS_CUSTOM_FOOTER: PartnerName[] = ['stash'];

// Partner list for custom nav logo
export const PARTNERS_CUSTOM_NAV_LOGO: PartnerName[] = [
  'beenverified',
  'bumper',
  'cbautogroup',
  'copilot',
  'experian',
  'fiona',
  'lyft',
  'nonda',
  'mint',
  'nonda',
  'stash',
  'wonolo',
];

/**
 * List of header/footer attributes to be used per partner
 * Keep in alphabetical order (except for default).
 */
const HeaderFooterContent = {
  default: {
    jerryLogoGrey: false,
  },
  beenverified: {
    subHeaderText: 'In collaboration with',
  },
  benzinga: {
    jerryLogoGrey: true,
    subHeaderText: 'Thanks for coming from Benzinga',
    footerLogoGrey: true,
  },
  billfixers: {
    jerryLogoGrey: true,
    subHeaderText: 'In collaboration with BillFixers',
  },
  bumper: {
    subHeaderText: 'In collaboration with',
  },
  cbautogroup: {
    jerryLogoGrey: true,
    subHeaderText: 'In collaboration with',
    noShowFooterLogo: true,
  },
  copilot: {
    jerryLogoGrey: true,
    subHeaderText: 'In collaboration with',
    footerLogoGrey: true,
  },
  dmv: {
    jerryLogoGrey: true,
    subHeaderText: 'Thanks for coming from DMV.org',
    footerLogoGrey: true,
  },
  experian: {
    logosSameLine: true,
  },
  fiona: {
    jerryLogoGrey: true,
    subHeaderText: 'In collaboration with',
    noShowFooterLogo: true,
  },
  iseecars: {
    jerryLogoGrey: true,
    subHeaderText: 'Thanks for coming from ISeeCars',
    footerLogoGrey: true,
  },
  jobble: {
    jerryLogoGrey: true,
    subHeaderText: 'Thanks for coming from Jobble',
    footerLogoGrey: true,
  },
  lyft: {
    navWhiteBackground: true,
    mainPartnerLogo: true,
    topNavbackgroundWhite: true,
    footer: (
      <>
        <Box mt={1}>
          The solicitation of car insurance is made by Lyft Center, Inc. a subsidiary of Lyft, Inc and a licensed
          insurance producer. Lyft Insurance Inc is a partner of Jerry Financial.
        </Box>
        <JerryFooter />
      </>
    ),
  },
  moneycrashers: {
    jerryLogoGrey: true,
    subHeaderText: 'In collaboration with MoneyCrashers',
    footerLogoGrey: true,
  },
  mint: {
    jerryLogoGrey: true,
    footerLogoGrey: true,
    logosSameLine: true,
  },
  mykukun: {
    jerryLogoGrey: true,
    subHeaderText: 'In collaboration with Kukun',
  },
  nerdwallet: {
    jerryLogoGrey: true,
    subHeaderText: 'Thanks for coming from NerdWallet',
    footerLogoGrey: false,
    noShowFooterLogo: true,
  },
  nonda: {
    jerryLogoGrey: true,
    subHeaderText: 'Thanks for coming from',
    footerLogoGrey: true,
  },
  stash: {
    subHeaderText: 'In collaboration with',
    footerText: 'Stash is a paid marketing partner of Jerry Insurance. See full terms ',
    footerModalButtonText: 'here.',
    footerModalTitle: 'Terms',
    footerModalBodyText:
      'Stash is a paid marketing partner of Jerry Insurance. Stash may receive compensation from business partners in connection with certain promotions in which Stash refers clients to such partners for the purchase of non-investment consumer products or services. This type of marketing partnership gives Stash an incentive to refer clients to business partners instead of to businesses that are not partners of Stash. This conflict of interest affects the ability of Stash to provide clients with unbiased, objective promotions concerning the products and services of its business partners. This could mean that the products and/or services of other businesses, that do not compensate Stash, may be more appropriate for a client than the products and/or services of Stash’s business partners. Clients are, however, not required to purchase the products and services Stash promotes.',
  },
  wonolo: {
    subHeaderText: 'In collaboration with',
  },
};

/**
 * Grab app container props per partner.
 * If step contains 'appContainerProps' defined, use it as override.
 * @param appData
 * @param pageProps
 */
export const getAppContainerProps = (
  appData: AppData,
  pageProps: Partial<AppContainerProps>,
  forceAppContainerProps?: boolean
): Partial<AppContainerProps> => {
  const partnerName: PartnerName | 'default' = appData.partner?.partner || 'default';
  // Set default
  const deafult = {
    jerryLogoGrey: false,
    subHeaderText: '',
    footerLogoGrey: false,
    noShowFooterLogo: false,
    logosSameLine: false,
    mainPartnerLogo: false,
    partnerLogo: null,
    fullBodyWidth: appData.stepSection === AppStepSection.Finished,
    noShowFooter: appData.flowType === FlowType.RepairEstimate || appData.flowType === FlowType.CarValue,
  };
  const partnerAppContainerProps = HeaderFooterContent[partnerName];
  const isPC = !includes([DeviceTypeType.Ios, DeviceTypeType.Android], appData.mobileOS);

  // Get partner logo
  const partnerLogo =
    partnerName !== 'default' && appData.partner && includes(PARTNERS_CUSTOM_NAV_LOGO, partnerName)
      ? getPartnerLogo(appData.partner)
      : null;

  // Get partner footer
  const appendFooter =
    partnerName !== 'default' && includes(PARTNERS_CUSTOM_FOOTER, partnerName) ? (
      <PartnerFooter
        footerText={partnerAppContainerProps.footerText}
        footerModalButtonText={partnerAppContainerProps.footerModalButtonText}
        footerModalTitle={partnerAppContainerProps.footerModalTitle}
        footerModalBodyText={partnerAppContainerProps.footerModalBodyText}
      />
    ) : null;

  const carValueConfig =
    appData.flowType === FlowType.CarValue &&
    !isPC &&
    growthBookInstance?.getFeatureValue(ABTestCarValueAdvanceDownload.storageKey, 0) === 2
      ? {
          footer: <></>,
          noShowFooterLogo: true,
        }
      : isGarageFlow(appData)
      ? {
          footer: (
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'47px'}>
              <Logo markOnly />
              &nbsp;&nbsp;&nbsp;&nbsp;Copyrights &copy; Jerry 2025. All rights reserved.
            </Box>
          ),
          noShowFooterLogo: true,
        }
      : {};
  let appContainerProps: Partial<AppContainerProps> = {};
  if (pageProps) {
    if (forceAppContainerProps) {
      appContainerProps = pick(pageProps, ['fullBodyWidth', 'noShowFooter', 'noShowNav', 'unsetMax']);
    }
    // If pageProps exist, check if partner falls under custom landing page enabled list,
    // also check if 'askPhoneNamePrefilled' enabled.
    if (includes(CUSTOM_LANDING_ENABLED_LIST, partnerName) && !appData.featureData?.askPhoneNamePrefilled) {
      appContainerProps = pick(pageProps, ['fullBodyWidth', 'noShowFooter']);
    }
    if (appData.isSeoFunnel || appData.internalSource === InternalSource.SEO) {
      appContainerProps = pick(pageProps, ['fullBodyWidth', 'noShowFooter']);
    }
  }

  return {
    ...deafult,
    ...partnerAppContainerProps,
    ...appContainerProps,
    partnerLogo,
    appendFooter,
    ...carValueConfig,
  };
};
